// src/components/pages/Auth/AccountUtils.js
import { getLangFromCookie, LANG_COOKIE } from "../../../Utils/LangCookie";
import { wpGraphql } from "../../../api/wpGraphql";

/* -------------------------- fonts by lang -------------------------- */
export const fonts = {
  EN: "LINE Seed Sans",
  ZH: "LINE Seed TW",
  JP: "LINE Seed JP",
};

/* ------------------------- lang helpers/text ------------------------ */
export const normalizeLang = (code) =>
  code === "EN" || code === "JP" ? code : "ZH";

export const getCurrentLangCode = () => {
  if (typeof window === "undefined") return "ZH";
  const fromCookie = getLangFromCookie();
  const fromLS = window.localStorage.getItem(LANG_COOKIE);
  return normalizeLang(fromCookie || fromLS || "ZH");
};

export const TEXT = {
  ZH: {
    memberArea: "會員專區",
    basicInfo: "更新會員基本資料",
    shoppingGuide: "購物說明",
    memberRights: "會員權益聲明",
    pageTitle: "SEHO 會員專區",

    loginPrompt: "請先登入以查看會員專區",
    statusPending: "待處理",
    quickBasicInfo: "更新會員基本資料",
    quickFavorites: "我的收藏",
    quickInquiryHistory: "詢價歷史",
    quickRetail: "線上報修",
    quickRetailHistory: "報修紀錄",

    basicTitle: "會員基本資料",
    basicUsernameLabel: "用戶名稱",
    basicPhoneLabel: "手機號碼",
    basicLandlinePhoneLabel: "市話",
    basicAddressLabel: "地址",
    basicLastName: "姓",
    basicFirstName: "名",
    basicBirthday: "生日",
    basicGender: "性別",
    genderMale: "男性",
    genderFemale: "女性",
    basicEmailLabel: "電子郵件",
    basicSaveButton: "儲存",
    basicUpdatedMessage: "已更新您的資料",
    basicUpdateFailed: "更新失敗，請稍後再試。",

    // OTP (change email)
    otpLabel: "Email 驗證碼",
    otpSend: "寄送驗證碼",
    otpResend: "重新寄送",
    otpHint: "更改電子郵件需要驗證。請輸入寄到新信箱的 6 位數驗證碼。",
    otpSent: "驗證碼已寄出。",
    otpVerified: "驗證成功。",
    otpNeedEmail: "請先輸入新的電子郵件。",
    otpRequired: "更改電子郵件需要驗證碼，請先寄送並輸入驗證碼。",
    otpExpired: "驗證碼已過期，請重新寄送。",
    otpInvalid: "驗證碼無效或已過期，請重新寄送。",
    otpRateLimited: "寄送太頻繁，請稍後再試。",
    otpNeedVerifyBeforeSave: "請先完成 Email 驗證後才能儲存。",

    // OTP (password change)
    otpNeedVerifyBeforeSubmitPwd: "請先完成 Email 驗證後才能修改密碼。",

    passwordTitle: "修改密碼",
    passwordOldLabel: "原密碼",
    passwordOldPlaceholder: "請輸入原密碼",
    passwordNewLabel: "新密碼",
    passwordNewPlaceholder: "請輸入新密碼",
    passwordHint: "密碼長度需為 8–20 碼，並符合下列規則。",
    passwordConfirmButton: "確認",
    passwordUpdatedMessage: "密碼已更新",
    passwordRulesNotMet: "請先符合所有密碼規則後再送出。",
    passwordFailed: "密碼更新失敗，請確認原密碼是否正確。",

    pwdRule1: "密碼長度需為 8–20 碼。",
    pwdRule3: "不可以與用戶名稱完全相同。",
    pwdRule4: "避免使用易猜測或公開資訊（例如 1234、電話、姓名）。",
    pwdRule5: "需同時包含數字、英文字母、特殊符號，並混合大小寫。",
  },
  EN: {
    memberArea: "Member Center",
    basicInfo: "Basic profile",
    shoppingGuide: "Shopping guide",
    memberRights: "Membership terms",
    pageTitle: "SEHO Member Center",

    loginPrompt: "Please log in to view your member area.",
    statusPending: "Pending",
    quickBasicInfo: "Basic profile",
    quickFavorites: "My favourites",
    quickInquiryHistory: "Inquiry history",
    quickRetail: "Online Repair",
    quickRetailHistory: "Repair history",

    basicTitle: "Basic profile",
    basicUsernameLabel: "Username",
    basicPhoneLabel: "Mobile number",
    basicLandlinePhoneLabel: "Landline phone number",
    basicAddressLabel: "Address",
    basicLastName: "Last name",
    basicFirstName: "First name",
    basicBirthday: "Date of birth",
    basicGender: "Gender",
    genderMale: "Male",
    genderFemale: "Female",
    basicEmailLabel: "Email",
    basicSaveButton: "Save",
    basicUpdatedMessage: "Your information has been updated.",
    basicUpdateFailed: "Update failed. Please try again later.",

    // OTP (change email)
    otpLabel: "Email OTP",
    otpSend: "Send OTP",
    otpResend: "Resend",
    otpHint:
      "Changing your email requires verification. Enter the 6-digit code sent to your new email.",
    otpSent: "OTP has been sent.",
    otpVerified: "Verified.",
    otpNeedEmail: "Please enter the new email first.",
    otpRequired:
      "Email OTP is required to change email. Please send and enter it.",
    otpExpired: "Code expired. Please resend.",
    otpInvalid: "Invalid or expired code. Please resend.",
    otpRateLimited: "Too many requests. Please try again later.",
    otpNeedVerifyBeforeSave: "Please verify your email OTP before saving.",

    // OTP (password change)
    otpNeedVerifyBeforeSubmitPwd:
      "Please verify your email OTP before changing password.",

    passwordTitle: "Change password",
    passwordOldLabel: "Current password",
    passwordOldPlaceholder: "Enter your current password",
    passwordNewLabel: "New password",
    passwordNewPlaceholder: "Enter your new password",
    passwordHint:
      "Password must be 8–20 characters and satisfy all rules below.",
    passwordConfirmButton: "Confirm",
    passwordUpdatedMessage: "Password has been updated.",
    passwordRulesNotMet: "Please satisfy all password rules before submitting.",
    passwordFailed:
      "Password update failed. Please confirm your current password.",

    pwdRule1: "Password length must be between 8–20 characters.",
    pwdRule3: "Password cannot be exactly the same as the username.",
    pwdRule4:
      "Avoid easy-to-guess or public info (e.g. 1234, phone number, name).",
    pwdRule5:
      "Must include numbers, English letters, special symbols / other scripts, and both upper & lower case.",
  },
  JP: {
    memberArea: "会員ページ",
    basicInfo: "会員基本情報",
    shoppingGuide: "ご利用ガイド",
    memberRights: "会員規約",
    pageTitle: "SEHO 会員ページ",

    loginPrompt: "ログインすると会員ページをご覧いただけます。",
    statusPending: "処理中",
    quickBasicInfo: "会員基本情報",
    quickFavorites: "お気に入り",
    quickInquiryHistory: "見積履歴",
    quickRetail: "オンライン修理受付",
    quickRetailHistory: "修理履歴",

    basicTitle: "会員基本情報",
    basicUsernameLabel: "ユーザー名",
    basicPhoneLabel: "携帯電話番号",
    basicLandlinePhoneLabel: "固定電話番号",
    basicAddressLabel: "住所",
    basicLastName: "姓",
    basicFirstName: "名",
    basicBirthday: "生年月日",
    basicGender: "性別",
    genderMale: "男性",
    genderFemale: "女性",
    basicEmailLabel: "メールアドレス",
    basicSaveButton: "保存する",
    basicUpdatedMessage: "会員情報を更新しました。",
    basicUpdateFailed:
      "更新に失敗しました。しばらくしてから再度お試しください。",

    // OTP (change email)
    otpLabel: "メール認証コード",
    otpSend: "送信",
    otpResend: "再送",
    otpHint:
      "メール変更には認証が必要です。新しいメールに届いた 6 桁のコードを入力してください。",
    otpSent: "認証コードを送信しました。",
    otpVerified: "認証しました。",
    otpNeedEmail: "先に新しいメールアドレスを入力してください。",
    otpRequired:
      "メール変更には認証コードが必要です。送信して入力してください。",
    otpExpired: "コードの有効期限が切れました。再送してください。",
    otpInvalid: "コードが無効、または期限切れです。再送してください。",
    otpRateLimited: "送信が多すぎます。しばらくしてから再試行してください。",
    otpNeedVerifyBeforeSave: "保存する前にメール認証を完了してください。",

    // OTP (password change)
    otpNeedVerifyBeforeSubmitPwd:
      "パスワード変更の前にメール認証を完了してください。",

    passwordTitle: "パスワード変更",
    passwordOldLabel: "現在のパスワード",
    passwordOldPlaceholder: "現在のパスワードを入力してください",
    passwordNewLabel: "新しいパスワード",
    passwordNewPlaceholder: "新しいパスワードを入力してください",
    passwordHint:
      "パスワードは 8〜20 文字で、以下の条件をすべて満たす必要があります。",
    passwordConfirmButton: "確認",
    passwordUpdatedMessage: "パスワードを更新しました。",
    passwordRulesNotMet: "すべての条件を満たしてから送信してください。",
    passwordFailed: "更新に失敗しました。現在のパスワードをご確認ください。",

    pwdRule1: "パスワードは 8〜20 文字で入力してください。",
    pwdRule3: "ユーザー名とまったく同じパスワードは使用できません。",
    pwdRule4: "1234・電話番号・氏名など推測されやすい情報は避けてください。",
    pwdRule5:
      "数字・英字・記号・その他の文字を使い、大文字と小文字を混在させてください。",
  },
};

export const tForLang = (lang, key) => {
  const l = normalizeLang(lang);
  return TEXT[l]?.[key] ?? TEXT.ZH?.[key] ?? key;
};

/* ----------------------- normalize WP viewer ------------------------ */
export function normalizeWpUser(viewer) {
  if (!viewer || !viewer.id) return null;

  return {
    ...viewer,
    userID: viewer.sehoUserID || viewer.userID || null,
    phoneNumber: viewer.phoneNumber || "",
    landlinePhoneNumber: viewer.landlinePhoneNumber || "",
    address: viewer.address || "",
    birthday: viewer.birthday || "",
    gender: viewer.gender || "male",
  };
}

/* ----------------------- GraphQL helpers --------------------------- */
export async function gqlFetchMyProfile() {
  const q = `
    query SehoMyProfile {
      viewer {
        id
        username
        email
        firstName
        lastName
        name
        sehoUserID
        phoneNumber
        landlinePhoneNumber
        address
        birthday
        gender
      }
    }
  `;

  return wpGraphql(
    q,
    {},
    {
      requestTag: "myProfile",
      operationName: "SehoMyProfile",
    },
  );
}

export async function gqlUpdateMyProfile(input) {
  const q = `
    mutation SehoUpdateMyProfile($input: SehoUpdateMyProfileInput!) {
      sehoUpdateMyProfile(input: $input) {
        ok
        message
        user {
          id
          username
          email
          firstName
          lastName
          name
          sehoUserID
          phoneNumber
          landlinePhoneNumber
          address
          birthday
          gender
        }
      }
    }
  `;

  return wpGraphql(
    q,
    { input },
    {
      requestTag: "updateMyProfile",
      operationName: "SehoUpdateMyProfile",
    },
  );
}

export async function gqlChangeMyPassword(input) {
  const q = `
    mutation SehoChangeMyPassword($input: SehoChangeMyPasswordInput!) {
      sehoChangeMyPassword(input: $input) {
        ok
        message
      }
    }
  `;
  return wpGraphql(
    q,
    { input },
    { requestTag: "changeMyPassword", operationName: "SehoChangeMyPassword" },
  );
}

/* ------------------------------ helpers ------------------------------ */
export function formatMMSS(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

export function sanitizeOtpInput(v) {
  const digits = String(v || "").replace(/\D/g, "");
  return digits.slice(0, 6);
}

/* ---------------------- password rule helpers ---------------------- */
export const initialPasswordRules = {
  length: false,
  notSameAsUsername: false,
  notEasyGuess: false,
  complexity: false,
};

function hasLongNumericSequence(str) {
  const s = str || "";
  if (s.length < 4) return false;

  let ascLen = 1;
  let descLen = 1;

  for (let i = 1; i < s.length; i++) {
    const prev = s[i - 1];
    const curr = s[i];

    if (/[0-9]/.test(prev) && /[0-9]/.test(curr)) {
      const diff = curr.charCodeAt(0) - prev.charCodeAt(0);
      if (diff === 1) {
        ascLen += 1;
        descLen = 1;
      } else if (diff === -1) {
        descLen += 1;
        ascLen = 1;
      } else {
        ascLen = 1;
        descLen = 1;
      }
      if (ascLen >= 4 || descLen >= 4) return true;
    } else {
      ascLen = 1;
      descLen = 1;
    }
  }

  return false;
}

export function evaluatePasswordRules(password, opts = {}) {
  const { username = "", phone = "" } = opts;

  const pwd = password || "";
  const uname = (username || "").toLowerCase();
  const phoneDigits = (phone || "").replace(/\D/g, "");

  const length = pwd.length >= 8 && pwd.length <= 20;
  const notSameAsUsername = !pwd || pwd.toLowerCase() !== uname;

  let notEasyGuess = !!pwd;
  if (pwd) {
    const lower = pwd.toLowerCase();
    const common = ["0000", "1111", "qwer", "password"];

    if (uname && uname.length >= 4 && lower.includes(uname))
      notEasyGuess = false;

    if (phoneDigits && phoneDigits.length >= 4) {
      const tail4 = phoneDigits.slice(-4);
      if (lower.includes(tail4)) notEasyGuess = false;
    }

    if (common.some((c) => lower.includes(c))) notEasyGuess = false;
    if (hasLongNumericSequence(pwd)) notEasyGuess = false;
    if (/(.)\1\1/.test(pwd)) notEasyGuess = false;
  }

  const hasAsciiLetter = /[A-Za-z]/.test(pwd);
  const hasDigit = /\d/.test(pwd);
  const hasSpecialAscii = /[^A-Za-z0-9]/.test(pwd);
  const hasNonLatin = /[^\u0000-\u007f]/.test(pwd);
  const hasSymbolOrOther = hasSpecialAscii || hasNonLatin;
  const hasUpper = /[A-Z]/.test(pwd);
  const hasLower = /[a-z]/.test(pwd);

  const complexity =
    !!pwd &&
    hasAsciiLetter &&
    hasDigit &&
    hasSymbolOrOther &&
    hasUpper &&
    hasLower;

  return { length, notSameAsUsername, notEasyGuess, complexity };
}

export const PASSWORD_RULE_ITEMS = [
  { key: "length", labelKey: "pwdRule1" },
  { key: "notSameAsUsername", labelKey: "pwdRule3" },
  { key: "notEasyGuess", labelKey: "pwdRule4" },
  { key: "complexity", labelKey: "pwdRule5" },
];

export function getPasswordStrength(
  password,
  rules = initialPasswordRules,
  opts = {},
) {
  const pwd = password || "";
  if (!pwd) return { level: "none", score: 0 };

  const { username = "" } = opts;
  const lower = pwd.toLowerCase();
  const uname = (username || "").toLowerCase();

  let score = 0;
  if (rules.length) score += 1;
  if (rules.complexity) score += 2;
  if (rules.notEasyGuess) score += 1;
  if (rules.notSameAsUsername) score += 1;
  if (pwd.length >= 12) score += 1;

  const hasAsciiLetter = /[A-Za-z]/.test(pwd);
  const hasDigit = /\d/.test(pwd);
  const hasSpecialAscii = /[^A-Za-z0-9]/.test(pwd);
  const hasNonLatin = /[^\u0000-\u007f]/.test(pwd);
  const hasSymbolOrOther = hasSpecialAscii || hasNonLatin;

  let typeCount = 0;
  [hasAsciiLetter, hasDigit, hasSymbolOrOther].forEach((b) => {
    if (b) typeCount += 1;
  });
  if (typeCount >= 2) score += 1;

  if (
    !rules.notEasyGuess ||
    !rules.notSameAsUsername ||
    (uname && lower === uname)
  ) {
    return { level: "weak", score };
  }

  let level = "weak";
  if (score >= 5) level = "strong";
  else if (score >= 3) level = "medium";

  return { level, score };
}

/* ----------------------- helper: display name ----------------------- */
export function getDisplayName(user) {
  if (!user) return "";
  const { firstName, lastName, name } = user || {};
  if (lastName || firstName) {
    if (lastName && firstName) return `${lastName}  ${firstName}`;
    return lastName || firstName;
  }
  return name || "";
}
